<template>
  <div class="space-y-6">
    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by reg ID, ward, area..."
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          v-model="selectedStatus"
          @change="handleSearch"
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option v-if="activeCatchingStatuses.length === 0" value="" disabled>No statuses configured</option>
          <option v-for="status in activeCatchingStatuses" :key="status.id" :value="status.id">
            {{ status.name }}
          </option>
        </select>
        <select
          v-model="selectedDistrict"
          @change="handleSearch"
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Districts</option>
          <option v-for="district in masterDataStore.districts" :key="district.id" :value="district.id">
            {{ district.name }}
          </option>
        </select>
        <select
          v-model="selectedColour"
          @change="handleSearch"
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Colours</option>
          <option v-for="colour in masterDataStore.colours" :key="colour.id" :value="colour.id">
            {{ colour.name }}
          </option>
        </select>
        <select
          v-model="selectedFitnessStatus"
          @change="handleSearch"
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Fitness</option>
          <option value="pending">Fitness Pending</option>
          <option value="fit">Fit</option>
          <option value="unfit">Unfit</option>
        </select>
        <button
          @click="handleSearch"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          🔍 Search
        </button>
        <button
          @click="showBulkImport = true"
          class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          📥 Bulk Import
        </button>
        <router-link
          to="/operations/catchings/new"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-center"
        >
          ➕ New Operation
        </router-link>
      </div>
    </div>

    <!-- List -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div v-if="selectedIds.length > 0" class="flex flex-col gap-3 border-b border-blue-100 bg-blue-50 px-6 py-4 md:flex-row md:items-center md:justify-between">
        <div class="text-sm font-medium text-blue-900">
          {{ selectedIds.length }} selected
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <select
            v-model="bulkFitnessStatus"
            class="px-3 py-2 border border-blue-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select fitness</option>
          <option value="fit">Mark Fit</option>
          <option value="unfit">Mark Unfit</option>
          <option value="pending">Mark Pending</option>
          </select>
          <button
            @click="applyBulkFitness"
            :disabled="!bulkFitnessStatus || catchingStore.loading"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Apply
          </button>
          <button
            @click="clearSelection"
            class="px-4 py-2 border border-blue-200 bg-white text-blue-700 rounded-lg text-sm hover:bg-blue-100"
          >
            Clear
          </button>
        </div>
      </div>
      <div v-if="catchingStore.loading" class="p-12 text-center">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="catchingStore.catchings.length === 0" class="p-12 text-center text-gray-500">
        No operations found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
           <thead class="bg-gray-100 border-b">
             <tr>
               <th class="px-6 py-3 text-left text-sm font-semibold">
                 <input
                   type="checkbox"
                   :checked="isCurrentPageSelected"
                   :indeterminate.prop="isCurrentPagePartiallySelected"
                   @change="toggleCurrentPageSelection"
                   class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                 />
               </th>
               <th v-for="column in sortableColumns" :key="column.key" class="px-6 py-3 text-left text-sm font-semibold">
                 <button
                   type="button"
                   @click="sortByColumn(column.key)"
                   class="inline-flex items-center gap-1 font-semibold hover:text-blue-700"
                 >
                   {{ column.label }}
                   <span class="text-xs text-gray-500">{{ sortIndicator(column.key) }}</span>
                 </button>
               </th>
               <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
             </tr>
           </thead>
           <tbody class="divide-y">
             <tr v-for="catching in catchingStore.catchings" :key="catching.id" class="hover:bg-gray-50">
               <td class="px-6 py-4 text-sm">
                 <input
                   type="checkbox"
                   :checked="selectedIds.includes(Number(catching.id))"
                   @change="toggleSelection(catching.id)"
                   class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                 />
               </td>
               <td class="px-6 py-4 text-sm font-medium">{{ catching.animal_reg_id }}</td>
               <td class="px-6 py-4 text-sm">
                 <span v-if="catching.animal" class="capitalize">{{ catching.animal.name }}</span>
                 <span v-else class="text-gray-400">—</span>
               </td>
               <td class="px-6 py-4 text-sm">
                 <span v-if="catching.colour">{{ catching.colour.name }}</span>
                 <span v-else class="text-gray-400">—</span>
               </td>
               <td class="px-6 py-4 text-sm capitalize">{{ catching.sex || '—' }}</td>
               <td class="px-6 py-4 text-sm">
                 <div>{{ catching.ward }}</div>
                 <div v-if="catching.area" class="text-xs text-gray-500">{{ catching.area }}</div>
               </td>
               <td class="px-6 py-4 text-sm">
                 <span v-if="catching.district">{{ catching.district.name }}</span>
                 <span v-else class="text-gray-400">—</span>
               </td>
               <td class="px-6 py-4 text-sm">{{ new Date(catching.catching_date).toLocaleDateString() }}</td>
               <td class="px-6 py-4 text-sm">
                 <span v-if="catching.created_by">{{ catching.created_by.full_name }}</span>
                 <span v-else class="text-gray-400">—</span>
               </td>
               <td class="px-6 py-4 text-sm">
                 <span :class="[
                   'px-3 py-1 rounded-full text-xs font-semibold capitalize',
                   catching.status === 'caught' ? 'bg-green-100 text-green-800' :
                   catching.status === 'released' ? 'bg-blue-100 text-blue-800' :
                   catching.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                 ]">
                   {{ statusLabel(catching) }}
                 </span>
               </td>
               <td class="px-6 py-4 text-sm">
                 <span :class="fitnessBadgeClass(catching.fitness_status)">
                   {{ fitnessLabel(catching.fitness_status) }}
                 </span>
               </td>
               <td class="px-6 py-4 text-sm">
                 <div class="flex items-center gap-2">
                 <button
                   @click="viewCatching(catching)"
                   type="button"
                   class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-100 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                   title="View catching details"
                   aria-label="View catching details"
                 >
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                     <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                     <circle cx="12" cy="12" r="3" />
                   </svg>
                   <span class="sr-only">View</span>
                 </button>
                 <button
                   @click="deleteCatchingFunc(catching.id)"
                   type="button"
                   class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-700 shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:bg-red-100 hover:shadow focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                   title="Delete catching"
                   aria-label="Delete catching"
                 >
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                     <path d="M3 6h18" />
                     <path d="M8 6V4h8v2" />
                     <path d="M19 6l-1 14H6L5 6" />
                     <path d="M10 11v5" />
                     <path d="M14 11v5" />
                   </svg>
                   <span class="sr-only">Delete</span>
                 </button>
                 </div>
               </td>
             </tr>
           </tbody>
         </table>
      </div>

      <!-- Pagination -->
      <div class="flex flex-col gap-3 px-6 py-4 border-t bg-gray-50 md:flex-row md:items-center md:justify-between">
        <div class="flex items-center gap-3 text-sm text-gray-600">
          <span>Rows per page</span>
          <select
            v-model.number="selectedPerPage"
            @change="changePerPage"
            class="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
          </select>
          <span v-if="catchingStore.pagination.total > 0">
            Showing {{ showingFrom }}-{{ showingTo }} of {{ catchingStore.pagination.total }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
        <button
          @click="previousPage"
          :disabled="catchingStore.pagination.page === 1"
          class="px-4 py-2 border border-gray-300 rounded bg-white disabled:opacity-50"
        >
          Previous
        </button>
        <button
          v-for="pageNumber in visiblePageNumbers"
          :key="pageNumber"
          @click="goToPage(pageNumber)"
          :class="[
            'min-w-[38px] px-3 py-2 border rounded text-sm',
            pageNumber === catchingStore.pagination.page
              ? 'border-blue-600 bg-blue-600 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ pageNumber }}
        </button>
        <button
          @click="nextPage"
          :disabled="catchingStore.pagination.page === catchingStore.pagination.totalPages"
          class="px-4 py-2 border border-gray-300 rounded bg-white disabled:opacity-50"
        >
          Next
        </button>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div v-if="showViewModal" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50; overflow-y: auto; padding: 20px 0;">
      <div style="background: white; padding: 24px; border-radius: 12px; width: 640px; max-height: 90vh; overflow-y: auto; position: relative; z-index: 51;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h3 style="font-size: 20px; font-weight: bold;">📋 Catching Details</h3>
          <button @click="showViewModal = false" style="font-size: 20px; background: none; border: none; cursor: pointer; color: #666;">✕</button>
        </div>

        <div v-if="viewItem">
          <div style="display: flex; gap: 8px; border-bottom: 1px solid #e5e7eb; margin-bottom: 16px;">
            <button
              @click="activeViewTab = 'details'"
              :style="modalTabStyle('details')"
            >
              Catching Details
            </button>
            <button
              v-if="viewStatusForm.fitness_status === 'fit'"
              @click="activeViewTab = 'surgery'"
              :style="modalTabStyle('surgery')"
            >
              Surgery Info
            </button>
            <button
              v-if="viewStatusForm.fitness_status === 'fit'"
              @click="activeViewTab = 'post_surgery'"
              :style="modalTabStyle('post_surgery')"
            >
              Post Surgery
            </button>
            <button
              v-if="isViewItemReleased"
              @click="activeViewTab = 'released'"
              :style="modalTabStyle('released')"
            >
              Released
            </button>
          </div>

          <div v-if="activeViewTab === 'details'" style="display: flex; flex-direction: column; gap: 16px;">
          <!-- Reg ID & Status -->
          <div style="display: grid; grid-template-columns: 1fr auto auto; gap: 12px; align-items: start; padding: 12px; background: #f9fafb; border-radius: 8px;">
            <div>
              <div style="font-size: 12px; color: #6b7280;">Registration ID</div>
              <div style="font-size: 18px; font-weight: bold;">{{ viewItem.animal_reg_id }}</div>
            </div>
            <div>
              <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Status</label>
              <select
                v-model="viewStatusForm.status"
                @change="updateViewStatus"
                :disabled="savingViewStatus"
                :style="statusSelectStyle(viewStatusForm.status)"
              >
                <option v-if="activeCatchingStatuses.length === 0" value="" disabled>No statuses configured</option>
                <option v-for="status in activeCatchingStatuses" :key="status.id" :value="status.id">
                  {{ status.name }}
                </option>
              </select>
            </div>
            <div>
              <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Fitness</label>
              <select
                v-model="viewStatusForm.fitness_status"
                @change="handleViewFitnessChange"
                :disabled="savingViewStatus"
                :style="fitnessSelectStyle(viewStatusForm.fitness_status)"
              >
                <option value="pending">Pending</option>
                <option value="fit">Fit</option>
                <option value="unfit">Unfit</option>
              </select>
            </div>
            <div v-if="viewStatusForm.fitness_status === 'unfit'" style="grid-column: 1 / -1;">
              <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Reason for Unfit *</label>
              <textarea
                v-model="viewStatusForm.unfit_reason"
                rows="2"
                placeholder="Enter reason"
                style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px; resize: vertical;"
              ></textarea>
              <div style="display: flex; justify-content: flex-end; margin-top: 8px;">
                <button
                  @click="updateViewStatus"
                  :disabled="savingViewStatus"
                  style="padding: 7px 14px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px;"
                  :style="{ opacity: savingViewStatus ? 0.6 : 1 }"
                >
                  {{ savingViewStatus ? 'Saving...' : 'Save Reason' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Animal Details -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Animal</div>
              <div style="font-weight: 500; text-transform: capitalize;">{{ viewItem.animal?.name || '—' }}</div>
            </div>
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Colour</div>
              <div style="font-weight: 500;">{{ viewItem.colour?.name || '—' }}</div>
            </div>
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Sex</div>
              <div style="font-weight: 500; text-transform: capitalize;">{{ viewItem.sex || '—' }}</div>
            </div>
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Date</div>
              <div style="font-weight: 500;">{{ viewItem.catching_date ? new Date(viewItem.catching_date).toLocaleDateString() : '—' }}</div>
            </div>
          </div>

          <!-- Location -->
          <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
            <div style="font-size: 14px; font-weight: 600; margin-bottom: 8px;">📍 Location</div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 14px;">
              <div><span style="color: #6b7280;">District:</span> {{ viewItem.district?.name || '—' }}</div>
              <div><span style="color: #6b7280;">Ward:</span> {{ viewItem.ward || '—' }}</div>
              <div><span style="color: #6b7280;">Area:</span> {{ viewItem.area || '—' }}</div>
              <div><span style="color: #6b7280;">GPS:</span> <span style="font-family: monospace; font-size: 12px;">{{ viewItem.latitude }}, {{ viewItem.longitude }}</span></div>
            </div>
          </div>

          <!-- Creator -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Created By</div>
              <div style="font-weight: 500;">{{ viewItem.created_by?.full_name || '—' }}</div>
            </div>
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Created At</div>
              <div style="font-weight: 500;">{{ viewItem.created_at ? new Date(viewItem.created_at).toLocaleString() : '—' }}</div>
            </div>
          </div>

          <!-- Remarks -->
          <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
            <div style="font-size: 12px; color: #6b7280;">Remarks</div>
            <div style="font-weight: 500;">{{ viewItem.remarks || 'No remarks' }}</div>
          </div>

          <!-- Photos -->
          <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
            <div style="font-size: 14px; font-weight: 600; margin-bottom: 8px;">📷 Photos</div>
            <div v-if="loadingImages" style="color: #6b7280; font-size: 13px;">Loading images...</div>
            <div v-else-if="viewImages.length > 0" style="display: flex; flex-wrap: wrap; gap: 8px;">
              <img
                v-for="(img, index) in viewImages"
                :key="index"
                :src="img.image_url || (apiBaseUrl + '/' + img.image_path)"
                style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; cursor: pointer;"
                @click="openFullImage(img.image_url || (apiBaseUrl + '/' + img.image_path))"
                @error="$event.target.style.display = 'none'"
              />
            </div>
            <div v-else style="color: #9ca3af; font-size: 13px;">No photos uploaded</div>
          </div>

          </div>

          <div v-else-if="activeViewTab === 'surgery'" style="display: flex; flex-direction: column; gap: 16px;">
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Age *</label>
                  <input v-model="surgeryForm.animal_age" placeholder="e.g., 2 years" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Weight (kg) *</label>
                  <input v-model="surgeryForm.animal_weight" type="number" min="0" step="0.01" placeholder="e.g., 12.5" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Surgery Date *</label>
                  <input v-model="surgeryForm.surgery_date" type="date" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Date of ARV *</label>
                  <input v-model="surgeryForm.arv_date" type="date" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div style="grid-column: 1 / -1;">
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Surgeon *</label>
                  <select v-model="surgeryForm.surgery_doctor_id" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;">
                    <option value="">Select Surgeon</option>
                    <option v-for="surgeon in activeSurgeons" :key="surgeon.id" :value="surgeon.id">
                      {{ surgeonLabel(surgeon) }}
                    </option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Date of Death</label>
                  <input v-model="surgeryForm.date_of_death" type="date" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Cause of Death</label>
                  <input v-model="surgeryForm.cause_of_death" placeholder="Required if died" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
                <div style="grid-column: 1 / -1; padding-top: 4px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                    <div style="font-size: 14px; font-weight: 600;">Medicines Used *</div>
                    <button
                      type="button"
                      @click="addSurgeryMedicine"
                      style="padding: 6px 10px; background: #16a34a; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px;"
                    >
                      + Add Medicine
                    </button>
                  </div>
                  <div v-if="activeMedicines.length === 0" style="font-size: 13px; color: #991b1b; padding: 8px; background: #fef2f2; border-radius: 6px;">
                    No active medicines found in medicines master.
                  </div>
                  <div v-for="(item, index) in surgeryMedicines" :key="index" style="display: grid; grid-template-columns: minmax(0, 1.6fr) 90px 90px minmax(0, 1fr) auto; gap: 8px; align-items: end; margin-bottom: 8px;">
                    <div>
                      <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Medicine</label>
                      <select v-model="item.medicine_id" @change="handleSurgeryMedicineChange(item)" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;">
                        <option value="">Select Medicine</option>
                        <option v-for="medicine in activeMedicines" :key="medicine.id" :value="medicine.id">
                          {{ medicineLabel(medicine) }}
                        </option>
                      </select>
                    </div>
                    <div>
                      <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Qty</label>
                      <input v-model="item.quantity" type="number" min="0" step="0.01" placeholder="Qty" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                    </div>
                    <div>
                      <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Unit</label>
                      <input v-model="item.unit" placeholder="Unit" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                    </div>
                    <div>
                      <label style="display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px;">Remarks</label>
                      <input v-model="item.remarks" placeholder="Optional" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                    </div>
                    <button
                      type="button"
                      @click="removeSurgeryMedicine(index)"
                      :disabled="surgeryMedicines.length === 1"
                      style="padding: 8px 10px; background: #dc2626; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px;"
                      :style="{ opacity: surgeryMedicines.length === 1 ? 0.5 : 1 }"
                    >
                      x
                    </button>
                  </div>
                </div>
              </div>
              <div style="display: flex; justify-content: flex-end; margin-top: 12px;">
                <button
                  @click="saveSurgeryInfo"
                  :disabled="savingSurgeryInfo"
                  style="padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;"
                  :style="{ opacity: savingSurgeryInfo ? 0.6 : 1 }"
                >
                  {{ savingSurgeryInfo ? 'Saving...' : 'Save Surgery Info' }}
                </button>
              </div>
            </div>

            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 14px; font-weight: 600; margin-bottom: 8px;">Surgery Photos</div>
              <div v-if="surgeryImages.length > 0" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px;">
                <div v-for="img in surgeryImages" :key="img.id" style="position: relative;">
                  <img
                    :src="img.image_url || (apiBaseUrl + '/' + img.image_path)"
                    style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; cursor: pointer;"
                    @click="openFullImage(img.image_url || (apiBaseUrl + '/' + img.image_path))"
                  />
                  <button
                    @click="deleteSurgeryImage(img.id)"
                    style="position: absolute; top: 4px; right: 4px; width: 24px; height: 24px; border: none; border-radius: 999px; background: #dc2626; color: white; cursor: pointer;"
                  >
                    x
                  </button>
                </div>
              </div>
              <div v-else style="color: #9ca3af; font-size: 13px; margin-bottom: 10px;">No surgery photos uploaded</div>
              <input
                type="file"
                multiple
                accept="image/*"
                :disabled="surgeryImages.length >= 2 || uploadingSurgeryPhotos"
                @change="handleSurgeryPhotoUpload"
                style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;"
              />
              <div style="font-size: 12px; color: #6b7280; margin-top: 6px;">
                Maximum 2 surgery photos. Uploaded: {{ surgeryImages.length }}/2
              </div>
            </div>
          </div>

          <div v-else-if="activeViewTab === 'post_surgery'" style="display: flex; flex-direction: column; gap: 16px;">
            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div style="grid-column: 1 / -1;">
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Post-Op Location *</label>
                  <select v-model="postSurgeryForm.location_id" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;">
                    <option value="">Select Location</option>
                    <option v-for="location in activePostSurgeryLocations" :key="location.id" :value="location.id">
                      {{ location.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">Start Date *</label>
                  <input v-model="postSurgeryForm.start_date" type="date" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                  <div style="font-size: 11px; color: #6d5dfc; margin-top: 4px;">Auto from surgery date</div>
                </div>
                <div>
                  <label style="display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px;">End Date *</label>
                  <input v-model="postSurgeryForm.end_date" type="date" style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;" />
                </div>
              </div>
            </div>

            <div style="display: flex; align-items: center; gap: 12px; color: #64748b; font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;">
              <div style="height: 1px; background: #d1d5db; flex: 1;"></div>
              <div>Daily Care Checklist</div>
              <div style="height: 1px; background: #d1d5db; flex: 1;"></div>
            </div>

            <div v-for="day in postSurgeryDays" :key="day.day_number" style="padding: 14px; background: #eef2ff; border: 1px solid #dbeafe; border-radius: 12px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                <div style="width: 28px; height: 28px; border-radius: 999px; background: #4f46e5; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px;">
                  {{ day.day_number === 3 ? '3+' : day.day_number }}
                </div>
                <div style="font-weight: 700; color: #4338ca;">{{ day.label }}</div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: start;">
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  <label v-for="item in day.checklistItems" :key="item.key" style="display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500;">
                    <input v-model="day.checklist[item.key]" type="checkbox" style="width: 16px; height: 16px;" />
                    {{ item.label }}
                  </label>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px;">
                    <label style="display: flex; align-items: center; gap: 8px; font-size: 13px;">
                      <input v-model="day.feed_morning" type="checkbox" />
                      Morning Feed
                    </label>
                    <label style="display: flex; align-items: center; gap: 8px; font-size: 13px;">
                      <input v-model="day.feed_evening" type="checkbox" />
                      Evening Feed
                    </label>
                  </div>
                </div>

                <div>
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                    <div style="font-size: 13px; font-weight: 700;">Medicines</div>
                    <button type="button" @click="addPostSurgeryMedicine(day)" style="padding: 4px 8px; background: #16a34a; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px;">
                      + Med
                    </button>
                  </div>
                  <div v-for="(medicine, index) in day.medicines" :key="index" style="display: grid; grid-template-columns: minmax(0, 1fr) 70px 64px auto; gap: 6px; margin-bottom: 6px;">
                    <select v-model="medicine.medicine_id" @change="handlePostSurgeryMedicineChange(medicine)" style="min-width: 0; padding: 7px; border: 1px solid #d1d5db; border-radius: 6px;">
                      <option value="">Medicine</option>
                      <option v-for="masterMedicine in activeMedicines" :key="masterMedicine.id" :value="masterMedicine.id">
                        {{ medicineLabel(masterMedicine) }}
                      </option>
                    </select>
                    <input v-model="medicine.quantity" type="number" min="0" step="0.01" placeholder="Qty" style="min-width: 0; padding: 7px; border: 1px solid #d1d5db; border-radius: 6px;" />
                    <input v-model="medicine.unit" placeholder="Unit" style="min-width: 0; padding: 7px; border: 1px solid #d1d5db; border-radius: 6px;" />
                    <button type="button" @click="removePostSurgeryMedicine(day, index)" style="padding: 7px 9px; background: #dc2626; color: white; border: none; border-radius: 6px; cursor: pointer;">x</button>
                  </div>
                  <div v-if="day.medicines.length === 0" style="font-size: 12px; color: #6b7280;">No medicines added</div>
                </div>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end;">
              <button
                @click="savePostSurgeryInfo"
                :disabled="savingPostSurgeryInfo"
                style="padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;"
                :style="{ opacity: savingPostSurgeryInfo ? 0.6 : 1 }"
              >
                {{ savingPostSurgeryInfo ? 'Saving...' : 'Save Post Surgery' }}
              </button>
            </div>
          </div>

          <div v-else-if="activeViewTab === 'released'" style="display: flex; flex-direction: column; gap: 16px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280;">Release Date & Time</div>
                <div style="font-weight: 500;">{{ formatReleaseDateTime(releaseDetails.dateTime) }}</div>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280;">Geo Location</div>
                <a
                  v-if="releaseMapUrl"
                  :href="releaseMapUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="font-weight: 500; color: #2563eb; text-decoration: underline; overflow-wrap: anywhere;"
                >
                  {{ releaseDetails.latitude }}, {{ releaseDetails.longitude }} — View on map
                </a>
                <div v-else style="font-weight: 500;">—</div>
              </div>
            </div>

            <div style="padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280;">Remarks</div>
              <div style="font-weight: 500; white-space: pre-wrap; overflow-wrap: anywhere;">{{ releaseDetails.remarks || 'No remarks' }}</div>
            </div>
          </div>

          <!-- Action buttons -->
          <div style="display: flex; gap: 8px; justify-content: flex-end; padding-top: 12px; border-top: 1px solid #e5e7eb;">
            <router-link
              :to="`/operations/catchings/${viewItem.id}`"
              style="padding: 8px 16px; background: #2563eb; color: white; border-radius: 6px; text-decoration: none; font-size: 14px;"
            >
              Full Details
            </router-link>
            <button
              @click="showViewModal = false"
              style="padding: 8px 16px; border: 1px solid #d1d5db; border-radius: 6px; cursor: pointer; font-size: 14px;"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Full Image Overlay -->
    <div v-if="fullImageUrl" @click="fullImageUrl = null" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 100; cursor: pointer;">
      <img :src="fullImageUrl" style="max-width: 90%; max-height: 90%; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.5);" />
    </div>

    <!-- Bulk Import Modal -->
    <div v-if="showBulkImport" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50;">
      <div style="background: white; padding: 24px; border-radius: 12px; width: 560px; max-height: 80vh; overflow-y: auto; position: relative; z-index: 51;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h3 style="font-size: 18px; font-weight: bold;">📥 Bulk Import Catchings</h3>
          <button @click="closeBulkImport" style="font-size: 20px; background: none; border: none; cursor: pointer; color: #666;">✕</button>
        </div>

        <div style="margin-bottom: 16px; padding: 12px; background: #eff6ff; border-radius: 8px; font-size: 13px; color: #1e40af;">
          <strong>CSV Format:</strong> Each row should have the following columns:<br/>
          <code style="font-size: 12px;">animal_reg_id, animal_id, colour_id, sex, ward, area, latitude, longitude, district_id, catching_date, status, remarks</code>
        </div>

        <div style="margin-bottom: 16px;">
          <button @click="downloadTemplate" style="padding: 6px 12px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; font-size: 13px;">
            📄 Download CSV Template
          </button>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Select CSV File</label>
          <input
            type="file"
            accept=".csv"
            @change="handleCsvFile"
            style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;"
          />
        </div>

        <div v-if="importPreview.length > 0" style="margin-bottom: 16px;">
          <p style="font-size: 14px; font-weight: 500; margin-bottom: 8px;">Preview ({{ importPreview.length }} records):</p>
          <div style="max-height: 200px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 4px;">
            <table style="width: 100%; font-size: 12px;">
              <thead style="background: #f9fafb; position: sticky; top: 0;">
                <tr>
                  <th style="padding: 4px 8px; text-align: left;">Reg ID</th>
                  <th style="padding: 4px 8px; text-align: left;">Sex</th>
                  <th style="padding: 4px 8px; text-align: left;">Ward</th>
                  <th style="padding: 4px 8px; text-align: left;">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in importPreview.slice(0, 10)" :key="i" style="border-top: 1px solid #f3f4f6;">
                  <td style="padding: 4px 8px;">{{ row.animal_reg_id }}</td>
                  <td style="padding: 4px 8px;">{{ row.sex }}</td>
                  <td style="padding: 4px 8px;">{{ row.ward }}</td>
                  <td style="padding: 4px 8px;">{{ row.status }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="importPreview.length > 10" style="padding: 4px 8px; font-size: 12px; color: #6b7280;">... and {{ importPreview.length - 10 }} more</p>
          </div>
        </div>

        <div v-if="importStatus" style="margin-bottom: 12px; padding: 8px 12px; border-radius: 6px; font-size: 13px;"
          :style="{ background: importError ? '#fef2f2' : '#f0fdf4', color: importError ? '#991b1b' : '#166534' }">
          {{ importStatus }}
        </div>

        <div style="display: flex; gap: 12px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
          <button @click="closeBulkImport" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;">Cancel</button>
          <button
            @click="executeBulkImport"
            :disabled="importPreview.length === 0 || importing"
            style="flex: 1; padding: 8px; background: #7c3aed; color: white; border: none; border-radius: 4px; cursor: pointer;"
            :style="{ opacity: importPreview.length === 0 || importing ? 0.5 : 1 }"
          >
            {{ importing ? 'Importing...' : `Import ${importPreview.length} Records` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useCatchingStore } from '@/stores/catchingStore'
import { useMasterDataStore } from '@/stores/masterDataStore'

const catchingStore = useCatchingStore()
const masterDataStore = useMasterDataStore()
const searchQuery = ref('')
const selectedStatus = ref('')
const selectedDistrict = ref('')
const selectedColour = ref('')
const selectedFitnessStatus = ref('')
const selectedSortBy = ref('catching_date')
const selectedSortDir = ref('desc')
const selectedPerPage = ref(catchingStore.pagination.perPage)
const perPageOptions = [10, 20, 50, 100, 200]

const sortableColumns = [
  { key: 'animal_reg_id', label: 'Reg ID' },
  { key: 'animal', label: 'Animal' },
  { key: 'colour', label: 'Colour' },
  { key: 'sex', label: 'Sex' },
  { key: 'location', label: 'Location' },
  { key: 'district', label: 'District' },
  { key: 'catching_date', label: 'Date' },
  { key: 'created_by', label: 'Created By' },
  { key: 'status', label: 'Status' },
  { key: 'fitness_status', label: 'Fitness' },
]

const applyFilters = (page = 1) => {
  catchingStore.setFilters({
    search: searchQuery.value,
    status: selectedStatus.value,
    fitness_status: selectedFitnessStatus.value,
    district_id: selectedDistrict.value,
    colour_id: selectedColour.value,
    sort_by: selectedSortBy.value,
    sort_dir: selectedSortDir.value,
  })
  return catchingStore.getCatchings(page)
}

const sortByColumn = (columnKey) => {
  if (selectedSortBy.value === columnKey) {
    selectedSortDir.value = selectedSortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    selectedSortBy.value = columnKey
    selectedSortDir.value = 'asc'
  }

  applyFilters(1)
}

const sortIndicator = (columnKey) => {
  if (selectedSortBy.value !== columnKey) return ''
  return selectedSortDir.value === 'asc' ? '^' : 'v'
}

const visiblePageNumbers = computed(() => {
  const current = catchingStore.pagination.page
  const total = catchingStore.pagination.totalPages
  const windowSize = 5
  const half = Math.floor(windowSize / 2)
  let start = Math.max(1, current - half)
  let end = Math.min(total, start + windowSize - 1)

  start = Math.max(1, end - windowSize + 1)

  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
})

const showingFrom = computed(() => {
  if (catchingStore.pagination.total === 0) return 0
  return (catchingStore.pagination.page - 1) * catchingStore.pagination.perPage + 1
})

const showingTo = computed(() => {
  return Math.min(catchingStore.pagination.page * catchingStore.pagination.perPage, catchingStore.pagination.total)
})

const selectedIds = ref([])
const bulkFitnessStatus = ref('')
const bulkUnfitReason = ref('')

const currentPageIds = computed(() => catchingStore.catchings.map(catching => Number(catching.id)))

const isCurrentPageSelected = computed(() => {
  return currentPageIds.value.length > 0 && currentPageIds.value.every(id => selectedIds.value.includes(id))
})

const isCurrentPagePartiallySelected = computed(() => {
  return currentPageIds.value.some(id => selectedIds.value.includes(id)) && !isCurrentPageSelected.value
})

const activeCatchingStatuses = computed(() => {
  return masterDataStore.statuses
    .filter(status => String(status.is_active) !== '0')
    .map(status => ({
      ...status,
      id: String(status.id || '').trim(),
      code: String(status.code || '').trim(),
      name: status.name || status.code,
      category: String(status.category || '').trim().toLowerCase(),
    }))
    .filter(status => status.id && status.code)
})

const statusCode = (value) => {
  const stringValue = String(value || '').trim()
  const status = activeCatchingStatuses.value.find(item => item.id === stringValue || item.code === stringValue)
  return status?.code || stringValue
}

const statusValue = (catching) => {
  const rawStatus = String(catching?.status || '').trim()
  if (rawStatus) {
    const status = activeCatchingStatuses.value.find(item => item.id === rawStatus || item.code === rawStatus)
    return status?.id || rawStatus
  }
  const masterStatusId = String(catching?.status_master?.id || '').trim()
  if (masterStatusId) return masterStatusId
  const statusId = String(catching?.status_id || '').trim()
  return statusId || ''
}

const statusLabel = (catchingOrValue) => {
  const value = typeof catchingOrValue === 'object' ? statusValue(catchingOrValue) : catchingOrValue
  const stringValue = String(value || '').trim()
  const status = activeCatchingStatuses.value.find(item => item.id === stringValue || item.code === stringValue)
  return status?.name || (typeof catchingOrValue === 'object' ? catchingOrValue?.status_master?.name || catchingOrValue?.status : stringValue) || 'Pending'
}

const toggleSelection = (id) => {
  const numericId = Number(id)
  selectedIds.value = selectedIds.value.includes(numericId)
    ? selectedIds.value.filter(selectedId => selectedId !== numericId)
    : [...selectedIds.value, numericId]
}

const toggleCurrentPageSelection = () => {
  if (isCurrentPageSelected.value) {
    selectedIds.value = selectedIds.value.filter(id => !currentPageIds.value.includes(id))
    return
  }

  selectedIds.value = Array.from(new Set([...selectedIds.value, ...currentPageIds.value]))
}

const clearSelection = () => {
  selectedIds.value = []
  bulkFitnessStatus.value = ''
  bulkUnfitReason.value = ''
}

const fitnessLabel = (status) => {
  const labels = {
    fit: 'Fit',
    unfit: 'Unfit',
    pending: 'Pending',
  }
  return labels[status] || 'Pending'
}

const fitnessBadgeClass = (status) => {
  const base = 'px-3 py-1 rounded-full text-xs font-semibold'
  if (status === 'fit') return `${base} bg-emerald-100 text-emerald-800`
  if (status === 'unfit') return `${base} bg-red-100 text-red-800`
  return `${base} bg-yellow-100 text-yellow-800`
}

const statusSelectStyle = (status) => {
  const code = statusCode(status)
  const colors = {
    caught: { background: '#dcfce7', color: '#166534' },
    released: { background: '#dbeafe', color: '#1e40af' },
    pending: { background: '#fef9c3', color: '#854d0e' },
    transferred: { background: '#f3f4f6', color: '#374151' },
  }
  const selected = colors[code] || colors.pending
  return {
    padding: '6px 34px 6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    border: 'none',
    outline: 'none',
    cursor: savingViewStatus.value ? 'not-allowed' : 'pointer',
    backgroundColor: selected.background,
    color: selected.color,
  }
}

const fitnessSelectStyle = (status) => {
  const colors = {
    fit: { background: '#dcfce7', color: '#166534' },
    unfit: { background: '#fee2e2', color: '#991b1b' },
    pending: { background: '#fef3c7', color: '#92400e' },
  }
  const selected = colors[status] || colors.pending
  return {
    padding: '6px 34px 6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    border: 'none',
    outline: 'none',
    cursor: savingViewStatus.value ? 'not-allowed' : 'pointer',
    backgroundColor: selected.background,
    color: selected.color,
  }
}

const applyBulkFitness = async () => {
  if (!bulkFitnessStatus.value || selectedIds.value.length === 0) return

  const label = fitnessLabel(bulkFitnessStatus.value)
  let unfitReason = ''

  if (bulkFitnessStatus.value === 'unfit') {
    const reasonResult = await Swal.fire({
      title: 'Reason for Unfit',
      input: 'textarea',
      inputPlaceholder: 'Enter reason for marking selected animals unfit',
      inputValue: bulkUnfitReason.value,
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Reason is required when marking animals unfit'
        return null
      },
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
    })

    if (!reasonResult.isConfirmed) return
    unfitReason = reasonResult.value.trim()
    bulkUnfitReason.value = unfitReason
  }

  const result = await Swal.fire({
    title: `Mark selected as ${label}?`,
    text: `${selectedIds.value.length} record(s) will be updated.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Update',
    cancelButtonText: 'Cancel',
  })

  if (!result.isConfirmed) return

  try {
    const response = await catchingStore.bulkUpdateFitness(selectedIds.value, bulkFitnessStatus.value, unfitReason)
    await applyFilters(catchingStore.pagination.page)
    clearSelection()
    Swal.fire({
      icon: 'success',
      title: 'Updated',
      text: `${response?.updated || 'Selected'} record(s) updated.`,
      confirmButtonColor: '#2563eb',
    })
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: error.response?.data?.message || catchingStore.error || 'Failed to update selected records',
      confirmButtonColor: '#2563eb',
    })
  }
}

// View modal
const showViewModal = ref(false)
const viewItem = ref(null)
const savingViewStatus = ref(false)
const savingSurgeryInfo = ref(false)
const savingPostSurgeryInfo = ref(false)
const uploadingSurgeryPhotos = ref(false)
const activeViewTab = ref('details')
const viewStatusForm = ref({
  status: 'pending',
  fitness_status: 'pending',
  unfit_reason: '',
})
const surgeryForm = ref({
  animal_age: '',
  animal_weight: '',
  surgery_date: '',
  arv_date: '',
  surgery_doctor_id: '',
  date_of_death: '',
  cause_of_death: '',
})
const createEmptySurgeryMedicine = () => ({
  medicine_id: '',
  quantity: '',
  unit: '',
  remarks: '',
})
const surgeryMedicines = ref([createEmptySurgeryMedicine()])
const postSurgeryForm = ref({
  location_id: '',
  start_date: '',
  end_date: '',
})
const postSurgeryChecklistGroups = [
  {
    day_number: 1,
    label: 'Day 1 Observations',
    checklistItems: [
      { key: 'suture_intact', label: 'Suture Intact' },
      { key: 'animal_active', label: 'Animal Active' },
      { key: 'no_fever', label: 'No Fever' },
    ],
  },
  {
    day_number: 2,
    label: 'Day 2 Observations',
    checklistItems: [
      { key: 'wound_intact', label: 'Wound Intact' },
      { key: 'no_fluid_collection', label: 'No Fluid Collection' },
    ],
  },
  {
    day_number: 3,
    label: 'Day 3 & Above',
    checklistItems: [
      { key: 'incision_closed', label: 'Incision Closed' },
    ],
  },
]
const createEmptyPostSurgeryMedicine = () => ({
  medicine_id: '',
  quantity: '',
  unit: '',
  remarks: '',
})
const createPostSurgeryDay = (group) => ({
  day_number: group.day_number,
  label: group.label,
  checklistItems: group.checklistItems,
  care_date: '',
  checklist: group.checklistItems.reduce((items, item) => {
    items[item.key] = true
    return items
  }, {}),
  feed_morning: false,
  feed_evening: false,
  remarks: '',
  medicines: [],
})
const postSurgeryDays = ref(postSurgeryChecklistGroups.map(createPostSurgeryDay))

const apiBaseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '/api')
const viewImages = ref([])
const loadingImages = ref(false)
const fullImageUrl = ref(null)

const surgeryImages = computed(() => {
  return viewImages.value.filter(image => image.image_type === 'surgery')
})

const activeMedicines = computed(() => {
  return masterDataStore.medicines.filter(medicine => String(medicine.is_active) !== '0')
})

const activePostSurgeryLocations = computed(() => {
  return masterDataStore.postSurgeryLocations.filter(location => String(location.is_active) !== '0')
})

const activeSurgeons = computed(() => {
  return masterDataStore.surgeons.filter(surgeon => String(surgeon.is_active) !== '0')
})

const isViewItemReleased = computed(() => statusCode(viewStatusForm.value.status) === 'released')

const releaseDetails = computed(() => {
  const catching = viewItem.value || {}
  const release = catching.release_details || catching.release || {}

  return {
    dateTime: release.released_at || release.release_datetime || release.release_date_time || release.release_date
      || catching.released_at || catching.release_datetime || catching.release_date_time || catching.release_date || '',
    latitude: release.latitude ?? release.release_latitude ?? catching.release_latitude ?? '',
    longitude: release.longitude ?? release.release_longitude ?? catching.release_longitude ?? '',
    remarks: release.remarks || release.release_remarks || catching.release_remarks || '',
  }
})

const releaseMapUrl = computed(() => {
  const latitude = Number(releaseDetails.value.latitude)
  const longitude = Number(releaseDetails.value.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return ''
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return ''
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`
})

const formatReleaseDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const medicineLabel = (medicine) => {
  const shortName = medicine.short_name ? ` (${medicine.short_name})` : ''
  const unit = medicine.unit ? ` - ${medicine.unit}` : ''
  return `${medicine.name}${shortName}${unit}`
}

const surgeonLabel = (surgeon) => {
  const designation = surgeon.designation ? ` - ${surgeon.designation}` : ''
  const regNo = surgeon.reg_no ? ` (${surgeon.reg_no})` : ''
  return `${surgeon.name}${designation}${regNo}`
}

const handleSurgeryMedicineChange = (item) => {
  const medicine = masterDataStore.medicines.find(m => String(m.id) === String(item.medicine_id))
  item.unit = medicine?.unit || ''
}

const addSurgeryMedicine = () => {
  surgeryMedicines.value.push(createEmptySurgeryMedicine())
}

const removeSurgeryMedicine = (index) => {
  if (surgeryMedicines.value.length === 1) return
  surgeryMedicines.value.splice(index, 1)
}

const hydrateSurgeryMedicines = (items = []) => {
  const mapped = items.map(item => ({
    medicine_id: item.medicine_id || '',
    quantity: item.quantity || '',
    unit: item.unit || item.medicine_master_unit || '',
    remarks: item.remarks || '',
  }))
  surgeryMedicines.value = mapped.length > 0 ? mapped : [createEmptySurgeryMedicine()]
}

const normalizedSurgeryMedicines = () => {
  return surgeryMedicines.value
    .filter(item => item.medicine_id || item.quantity || item.unit || item.remarks)
    .map(item => ({
      medicine_id: item.medicine_id,
      quantity: item.quantity,
      unit: item.unit,
      remarks: item.remarks,
    }))
}

const addDays = (dateValue, days) => {
  if (!dateValue) return ''
  const date = new Date(`${dateValue}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

const hydratePostSurgery = (postSurgery = null, catching = {}) => {
  const defaultStartDate = catching.surgery_date || surgeryForm.value.surgery_date || ''
  postSurgeryForm.value = {
    location_id: postSurgery?.location_id || activePostSurgeryLocations.value[0]?.id || '',
    start_date: postSurgery?.start_date || defaultStartDate,
    end_date: postSurgery?.end_date || addDays(defaultStartDate, 3),
  }

  const existingDays = postSurgery?.days || []
  postSurgeryDays.value = postSurgeryChecklistGroups.map(group => {
    const existing = existingDays.find(day => Number(day.day_number) === group.day_number)
    const day = createPostSurgeryDay(group)
    if (!existing) return day

    day.care_date = existing.care_date || ''
    day.checklist = { ...day.checklist, ...(existing.checklist || {}) }
    day.feed_morning = String(existing.feed_morning) === '1' || existing.feed_morning === true
    day.feed_evening = String(existing.feed_evening) === '1' || existing.feed_evening === true
    day.remarks = existing.remarks || ''
    day.medicines = (existing.medicines || []).map(medicine => ({
      medicine_id: medicine.medicine_id || '',
      quantity: medicine.quantity || '',
      unit: medicine.unit || medicine.medicine_master_unit || '',
      remarks: medicine.remarks || '',
    }))
    return day
  })
}

const addPostSurgeryMedicine = (day) => {
  day.medicines.push(createEmptyPostSurgeryMedicine())
}

const removePostSurgeryMedicine = (day, index) => {
  day.medicines.splice(index, 1)
}

const handlePostSurgeryMedicineChange = (item) => {
  const medicine = masterDataStore.medicines.find(m => String(m.id) === String(item.medicine_id))
  item.unit = medicine?.unit || ''
}

const normalizedPostSurgeryDays = () => {
  return postSurgeryDays.value.map(day => ({
    day_number: day.day_number,
    care_date: day.care_date || '',
    checklist: day.checklist,
    feed_morning: day.feed_morning,
    feed_evening: day.feed_evening,
    remarks: day.remarks || '',
    medicines: day.medicines
      .filter(item => item.medicine_id || item.quantity || item.unit || item.remarks)
      .map(item => ({
        medicine_id: item.medicine_id,
        quantity: item.quantity,
        unit: item.unit,
        remarks: item.remarks,
      })),
  }))
}

const modalTabStyle = (tab) => ({
  padding: '10px 12px',
  border: 'none',
  borderBottom: activeViewTab.value === tab ? '2px solid #2563eb' : '2px solid transparent',
  color: activeViewTab.value === tab ? '#1d4ed8' : '#4b5563',
  background: 'transparent',
  fontWeight: '600',
  cursor: 'pointer',
})

const viewCatching = async (catching) => {
  viewItem.value = catching
  activeViewTab.value = 'details'
  viewStatusForm.value = {
    status: statusValue(catching),
    fitness_status: catching.fitness_status || 'pending',
    unfit_reason: catching.unfit_reason || '',
  }
  surgeryForm.value = {
    animal_age: catching.animal_age || '',
    animal_weight: catching.animal_weight || '',
    surgery_date: catching.surgery_date || '',
    arv_date: catching.arv_date || '',
    surgery_doctor_id: catching.surgery_doctor_id || '',
    date_of_death: catching.date_of_death || '',
    cause_of_death: catching.cause_of_death || '',
  }
  hydrateSurgeryMedicines(catching.surgery_medicines || [])
  hydratePostSurgery(catching.post_surgery || null, catching)
  viewImages.value = []
  showViewModal.value = true

  loadingImages.value = true
  try {
    const images = await catchingStore.getImages(catching.id)
    viewImages.value = images || []
  } catch (e) {
    console.error('Failed to load images:', e)
    viewImages.value = []
  } finally {
    loadingImages.value = false
  }
}

const openFullImage = (url) => {
  fullImageUrl.value = url
}

// Delete
const deleteCatchingFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this catching operation?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await catchingStore.deleteCatching(id)
        await catchingStore.getCatchings(catchingStore.pagination.page)
        Swal.fire({ icon: 'success', title: 'Deleted', text: 'Catching operation deleted', confirmButtonColor: '#3085d6' })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: error.response?.data?.message || catchingStore.error || 'Failed to delete catching operation',
          confirmButtonColor: '#3085d6'
        })
      }
    }
  })
}

const updateViewStatus = async () => {
  if (!viewItem.value) return

  // Snapshot both IDs before the async request. Refreshing the list or replacing
  // viewItem must not change which catching record/status the user selected.
  const catchingId = Number(viewItem.value.id)
  const selectedStatusId = String(viewStatusForm.value.status || '').trim()

  if (!catchingId || !selectedStatusId) {
    Swal.fire({
      icon: 'warning',
      title: 'Status Required',
      text: 'Please select a valid status.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  if (viewStatusForm.value.fitness_status === 'unfit' && !viewStatusForm.value.unfit_reason.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Reason Required',
      text: 'Please enter a reason when marking the animal unfit.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  savingViewStatus.value = true
  try {
    const updated = await catchingStore.updateCatching(catchingId, {
      status_id: selectedStatusId,
      status: selectedStatusId,
      fitness_status: viewStatusForm.value.fitness_status,
      unfit_reason: viewStatusForm.value.fitness_status === 'unfit'
        ? viewStatusForm.value.unfit_reason.trim()
        : '',
    })

    viewItem.value = updated
    viewStatusForm.value = {
      // Keep the selected master ID even if an older API response represents
      // status by code or omits its relation.
      status: selectedStatusId,
      fitness_status: updated.fitness_status || 'pending',
      unfit_reason: updated.unfit_reason || '',
    }
    await applyFilters(catchingStore.pagination.page)
    Swal.fire({
      icon: 'success',
      title: 'Updated',
      text: 'Status updated successfully.',
      confirmButtonColor: '#2563eb',
    })
  } catch (error) {
    const responseErrors = error.response?.data?.errors
    const errorDetail = responseErrors && typeof responseErrors === 'object'
      ? Object.values(responseErrors).filter(Boolean).join(' ')
      : (typeof responseErrors === 'string' ? responseErrors : '')
    Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: errorDetail || error.response?.data?.message || catchingStore.error || 'Failed to update status',
      confirmButtonColor: '#2563eb',
    })
  } finally {
    savingViewStatus.value = false
  }
}

const handleViewFitnessChange = () => {
  if (viewStatusForm.value.fitness_status === 'unfit') {
    activeViewTab.value = 'details'
    return
  }
  if (viewStatusForm.value.fitness_status !== 'fit') {
    activeViewTab.value = 'details'
  }
  updateViewStatus()
}

const saveSurgeryInfo = async () => {
  if (!viewItem.value) return

  if (!surgeryForm.value.animal_age || !surgeryForm.value.animal_weight || !surgeryForm.value.surgery_date || !surgeryForm.value.arv_date || !surgeryForm.value.surgery_doctor_id) {
    Swal.fire({
      icon: 'warning',
      title: 'Missing Fields',
      text: 'Age, weight, surgery date, ARV date, and surgeon are required.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  if (surgeryForm.value.date_of_death && !surgeryForm.value.cause_of_death.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Cause Required',
      text: 'Cause of death is required when date of death is filled.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  const medicinesPayload = normalizedSurgeryMedicines()
  if (medicinesPayload.length === 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Medicine Required',
      text: 'Please add at least one medicine used during surgery.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  const invalidMedicine = medicinesPayload.some(item => !item.medicine_id || !item.quantity || Number(item.quantity) <= 0)
  if (invalidMedicine) {
    Swal.fire({
      icon: 'warning',
      title: 'Medicine Details Required',
      text: 'Please select a medicine and enter quantity for every medicine row.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  savingSurgeryInfo.value = true
  try {
    const updated = await catchingStore.updateCatching(viewItem.value.id, {
      animal_age: surgeryForm.value.animal_age,
      animal_weight: surgeryForm.value.animal_weight,
      surgery_date: surgeryForm.value.surgery_date,
      arv_date: surgeryForm.value.arv_date,
      surgery_doctor_id: surgeryForm.value.surgery_doctor_id,
      date_of_death: surgeryForm.value.date_of_death || '',
      cause_of_death: surgeryForm.value.cause_of_death || '',
      surgery_medicines: medicinesPayload,
    })

    viewItem.value = updated
    hydrateSurgeryMedicines(updated.surgery_medicines || [])
    Swal.fire({
      icon: 'success',
      title: 'Saved',
      text: 'Surgery information saved successfully.',
      confirmButtonColor: '#2563eb',
    })
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Save Failed',
      text: error.response?.data?.message || catchingStore.error || 'Failed to save surgery information',
      confirmButtonColor: '#2563eb',
    })
  } finally {
    savingSurgeryInfo.value = false
  }
}

const handleSurgeryPhotoUpload = async (event) => {
  const files = Array.from(event.target.files || [])
  if (!viewItem.value || files.length === 0) return

  const remaining = 2 - surgeryImages.value.length
  if (files.length > remaining) {
    Swal.fire({
      icon: 'warning',
      title: 'Too Many Photos',
      text: `You can upload only ${remaining} more surgery photo(s).`,
      confirmButtonColor: '#2563eb',
    })
    event.target.value = ''
    return
  }

  const formData = new FormData()
  files.forEach(file => formData.append('images', file))
  formData.append('image_type', 'surgery')

  uploadingSurgeryPhotos.value = true
  try {
    await catchingStore.uploadImages(viewItem.value.id, formData)
    const images = await catchingStore.getImages(viewItem.value.id)
    viewImages.value = images || []
    event.target.value = ''
    Swal.fire({
      icon: 'success',
      title: 'Uploaded',
      text: 'Surgery photo uploaded successfully.',
      confirmButtonColor: '#2563eb',
    })
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Upload Failed',
      text: error.response?.data?.message || catchingStore.error || 'Failed to upload surgery photo',
      confirmButtonColor: '#2563eb',
    })
  } finally {
    uploadingSurgeryPhotos.value = false
  }
}

const deleteSurgeryImage = async (imageId) => {
  await catchingStore.deleteImage(viewItem.value.id, imageId)
  const images = await catchingStore.getImages(viewItem.value.id)
  viewImages.value = images || []
}

const savePostSurgeryInfo = async () => {
  if (!viewItem.value) return

  if (!postSurgeryForm.value.location_id || !postSurgeryForm.value.start_date || !postSurgeryForm.value.end_date) {
    Swal.fire({
      icon: 'warning',
      title: 'Missing Fields',
      text: 'Post-op location, start date, and end date are required.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  if (new Date(postSurgeryForm.value.end_date) < new Date(postSurgeryForm.value.start_date)) {
    Swal.fire({
      icon: 'warning',
      title: 'Invalid Date',
      text: 'End date must be on or after start date.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  const days = normalizedPostSurgeryDays()
  const invalidMedicine = days.some(day => day.medicines.some(item => !item.medicine_id || !item.quantity || Number(item.quantity) <= 0))
  if (invalidMedicine) {
    Swal.fire({
      icon: 'warning',
      title: 'Medicine Details Required',
      text: 'Please select a medicine and enter quantity for every post surgery medicine row.',
      confirmButtonColor: '#2563eb',
    })
    return
  }

  savingPostSurgeryInfo.value = true
  try {
    const updated = await catchingStore.updateCatching(viewItem.value.id, {
      post_surgery: {
        location_id: postSurgeryForm.value.location_id,
        start_date: postSurgeryForm.value.start_date,
        end_date: postSurgeryForm.value.end_date,
        days,
      },
    })

    viewItem.value = updated
    hydratePostSurgery(updated.post_surgery || null, updated)
    Swal.fire({
      icon: 'success',
      title: 'Saved',
      text: 'Post surgery information saved successfully.',
      confirmButtonColor: '#2563eb',
    })
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Save Failed',
      text: error.response?.data?.message || catchingStore.error || 'Failed to save post surgery information',
      confirmButtonColor: '#2563eb',
    })
  } finally {
    savingPostSurgeryInfo.value = false
  }
}

// Bulk import
const showBulkImport = ref(false)
const importPreview = ref([])
const importStatus = ref('')
const importError = ref(false)
const importing = ref(false)

const downloadTemplate = () => {
  const header = 'animal_reg_id,animal_id,colour_id,sex,ward,area,latitude,longitude,district_id,catching_date,status,remarks'
  const sample = 'REG000001,1,1,male,Ward 1,Main Street,12.345678,78.123456,1,2026-04-15,caught,Sample remark'
  const blob = new Blob([header + '\n' + sample + '\n'], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'catchings_import_template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

const handleCsvFile = (event) => {
  const file = event.target.files[0]
  if (!file) return
  importStatus.value = ''
  importError.value = false

  const reader = new FileReader()
  reader.onload = (e) => {
    const text = e.target.result
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0)
    if (lines.length < 2) {
      importStatus.value = 'CSV must have a header row and at least one data row.'
      importError.value = true
      return
    }

    const headers = lines[0].split(',').map(h => h.trim())
    const rows = []
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim())
      const obj = {}
      headers.forEach((h, idx) => { obj[h] = values[idx] || '' })
      rows.push(obj)
    }
    importPreview.value = rows
  }
  reader.readAsText(file)
}

const executeBulkImport = async () => {
  if (importPreview.value.length === 0) return
  importing.value = true
  importStatus.value = ''
  importError.value = false

  let success = 0
  let failed = 0

  for (const row of importPreview.value) {
    try {
      await catchingStore.createCatching({
        animal_reg_id: row.animal_reg_id || '',
        animal_id: row.animal_id || 1,
        colour_id: row.colour_id || null,
        sex: row.sex || '',
        ward: row.ward || '',
        area: row.area || '',
        latitude: row.latitude || 0,
        longitude: row.longitude || 0,
        district_id: row.district_id || null,
        catching_date: row.catching_date || new Date().toISOString().split('T')[0],
        status: row.status || 'caught',
        remarks: row.remarks || ''
      })
      success++
    } catch {
      failed++
    }
  }

  importing.value = false
  if (failed === 0) {
    importStatus.value = `✅ Successfully imported ${success} record(s).`
    importError.value = false
  } else {
    importStatus.value = `Imported ${success}, Failed ${failed}.`
    importError.value = true
  }

  await catchingStore.getCatchings(1)
}

const closeBulkImport = () => {
  showBulkImport.value = false
  importPreview.value = []
  importStatus.value = ''
  importError.value = false
}

const handleSearch = () => {
  applyFilters(1)
}

const nextPage = () => {
  applyFilters(catchingStore.pagination.page + 1)
}

const previousPage = () => {
  applyFilters(catchingStore.pagination.page - 1)
}

const goToPage = (page) => {
  if (page === catchingStore.pagination.page) return
  applyFilters(page)
}

const changePerPage = () => {
  catchingStore.setPerPage(selectedPerPage.value)
  applyFilters(1)
}

onMounted(() => {
  masterDataStore.getDistricts()
  masterDataStore.getColours()
  masterDataStore.getSurgeons()
  masterDataStore.getMedicines()
  masterDataStore.getStatuses()
  masterDataStore.getPostSurgeryLocations()
  applyFilters(1)
})
</script>
